import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  addDoc, 
  onSnapshot,
  serverTimestamp,
  and,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  or
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { formatDate } from '../utils/dateFormatters';

/**
 * Communications Inbox Component
 * 
 * Data Structure for communications collection:
 * {
 *   id: string,
 *   projectId: string,
 *   taskId?: string, // optional - for task-specific communications
 *   threadId?: string, // ID of the parent message if this is a reply
 *   messageType: 'pfi-customer' | 'pfi-supplier' | 'system-notification' | 'info-request',
 *   senderId: string, // user ID who sent the message
 *   senderType: 'pfi' | 'customer' | 'supplier' | 'system',
 *   senderName: string,
 *   senderEmail?: string,
 *   recipientIds: string[], // array of user IDs who should see this message
 *   recipientTypes: string[], // array of user types ['pfi', 'customer', 'supplier']
 *   subject: string,
 *   message: string,
 *   status: 'sent' | 'read' | 'replied',
 *   readBy?: string[], // array of user IDs who have read this message
 *   priority: 'low' | 'normal' | 'high',
 *   createdAt: timestamp,
 *   updatedAt: timestamp,
 *   relatedTo?: {
 *     type: 'quote-request' | 'info-request' | 'milestone' | 'general',
 *     referenceId?: string
 *   },
 *   attachments?: {
 *     id: string,
 *     url: string,
 *     filename: string,
 *     type: string
 *   }[],
 *   replyCount?: number // number of replies to this message
 * }
 */

const CommunicationsInbox = ({ 
  userType, // 'pfi' | 'customer' | 'supplier'
  userId, 
  projectId = null, // null for main inbox, specific ID for project inbox
  userName,
  userEmail 
}) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [newSubject, setNewSubject] = useState('');
  const [selectedRecipient, setSelectedRecipient] = useState('');
  const [showCompose, setShowCompose] = useState(false);
  const [availableRecipients, setAvailableRecipients] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [threadMessages, setThreadMessages] = useState([]);
  const [replyMessage, setReplyMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all'); // all, unread, starred

  useEffect(() => {
    const loadMessages = () => {
      let q;

      // Build query constraints based on user type
      // Simplified query to avoid index requirements initially
      
      if (userType === 'pfi') {
        // PFI sees all messages
        q = query(
          collection(db, 'communications'),
          orderBy('createdAt', 'desc')
        );
      } else if (userType === 'supplier') {
        // Supplier sees all messages and we'll filter client-side to avoid index requirements
        q = query(collection(db, 'communications'));
      } else if (userType === 'customer') {
        // Customer sees their project messages
        q = query(
          collection(db, 'communications'),
          where('projectId', '==', projectId),
          orderBy('createdAt', 'desc')
        );
      }

      const unsubscribe = onSnapshot(q, async (snapshot) => {
        const allMessages = await Promise.all(
          snapshot.docs.map(async (docSnap) => {
            const data = docSnap.data();
            
            // Count replies for each message (only for parent messages)
            let replyCount = 0;
            if (!data.threadId) {
              const repliesQuery = query(
                collection(db, 'communications'),
                where('threadId', '==', docSnap.id)
              );
              const repliesSnapshot = await getDocs(repliesQuery);
              replyCount = repliesSnapshot.size;
            }
            
            return {
              id: docSnap.id,
              ...data,
              replyCount: replyCount,
              isUnread: !data.readBy || !data.readBy.includes(userId)
            };
          })
        );
        
        // Filter to only show parent messages (not replies) in the main list
        let parentMessages = allMessages.filter(msg => !msg.threadId);
        
        // Filter by user type
        if (userType === 'supplier') {
          // Suppliers only see messages where they are recipients
          parentMessages = parentMessages.filter(msg => 
            msg.recipientIds && msg.recipientIds.includes(userId)
          );
        } else if (userType === 'customer') {
          // Customers only see messages from their project or sent by them
          parentMessages = parentMessages.filter(msg => 
            (msg.projectId === projectId && msg.recipientIds && msg.recipientIds.includes(userId)) ||
            msg.senderId === userId
          );
        }
        
        // For project-specific view, filter by projectId if needed
        let filteredMessages = projectId && userType === 'pfi' 
          ? parentMessages.filter(msg => msg.projectId === projectId)
          : parentMessages;
          
        // Sort messages by createdAt (most recent first) - especially important for suppliers
        // where we removed the orderBy from the query
        filteredMessages.sort((a, b) => {
          const aTime = a.createdAt?.toMillis ? a.createdAt.toMillis() : 
                       a.createdAt?.seconds ? a.createdAt.seconds * 1000 : 0;
          const bTime = b.createdAt?.toMillis ? b.createdAt.toMillis() : 
                       b.createdAt?.seconds ? b.createdAt.seconds * 1000 : 0;
          return bTime - aTime; // Descending order (newest first)
        });
        
        setMessages(filteredMessages);
        setLoading(false);
      });

      return unsubscribe;
    };

    const unsubscribe = loadMessages();
    return () => unsubscribe();
  }, [userType, userId, projectId]);

  // Load available recipients when compose form is opened
  useEffect(() => {
    const loadRecipients = async () => {
      if (!showCompose || userType !== 'pfi') return;
      
      try {
        const recipients = [];
        
        if (projectId) {
          // Load project-specific recipients
          const projectDoc = await getDoc(doc(db, 'projects', projectId));
          if (projectDoc.exists()) {
            const projectData = projectDoc.data();
            
            // Add customer if exists
            if (projectData.customerId) {
              const customerDoc = await getDoc(doc(db, 'customers', projectData.customerId));
              if (customerDoc.exists()) {
                recipients.push({
                  id: projectData.customerId,
                  name: customerDoc.data().name || 'Customer',
                  type: 'customer',
                  email: customerDoc.data().email
                });
              }
            }
            
            // Load suppliers associated with this project's tasks
            const tasksSnapshot = await getDocs(collection(db, 'projects', projectId, 'tasks'));
            const supplierIds = new Set();
            
            for (const taskDoc of tasksSnapshot.docs) {
              const taskData = taskDoc.data();
              // Check for invited suppliers
              if (taskData.invitedSuppliers && Array.isArray(taskData.invitedSuppliers)) {
                taskData.invitedSuppliers.forEach(supplierId => {
                  if (supplierId && typeof supplierId === 'string') {
                    supplierIds.add(supplierId);
                  }
                });
              }
              // Check for assigned suppliers
              if (taskData.assignedSuppliers && Array.isArray(taskData.assignedSuppliers)) {
                taskData.assignedSuppliers.forEach(supplierId => {
                  if (supplierId && typeof supplierId === 'string') {
                    supplierIds.add(supplierId);
                  }
                });
              }
              // Check for quotes
              const quotesSnapshot = await getDocs(collection(db, 'projects', projectId, 'tasks', taskDoc.id, 'quotes'));
              quotesSnapshot.docs.forEach(quoteDoc => {
                const quoteData = quoteDoc.data();
                if (quoteData.supplierId && typeof quoteData.supplierId === 'string') {
                  supplierIds.add(quoteData.supplierId);
                }
              });
            }
            
            // Load supplier details
            for (const supplierId of supplierIds) {
              try {
                const supplierDoc = await getDoc(doc(db, 'suppliers', supplierId));
                if (supplierDoc.exists()) {
                  recipients.push({
                    id: supplierId,
                    name: supplierDoc.data().name || 'Unnamed Supplier',
                    type: 'supplier',
                    email: supplierDoc.data().email || ''
                  });
                }
              } catch (err) {
                console.warn(`Could not load supplier ${supplierId}:`, err);
              }
            }
          }
        } else {
          // Load all suppliers and customers for general messaging
          const suppliersSnapshot = await getDocs(collection(db, 'suppliers'));
          suppliersSnapshot.docs.forEach(doc => {
            const data = doc.data();
            if (data && data.name) {
              recipients.push({
                id: doc.id,
                name: data.name,
                type: 'supplier',
                email: data.email || ''
              });
            }
          });
          
          const customersSnapshot = await getDocs(collection(db, 'customers'));
          customersSnapshot.docs.forEach(doc => {
            const data = doc.data();
            if (data && data.name) {
              recipients.push({
                id: doc.id,
                name: data.name,
                type: 'customer',
                email: data.email || ''
              });
            }
          });
        }
        
        setAvailableRecipients(recipients);
      } catch (error) {
        console.error('Error loading recipients:', error);
      }
    };
    
    loadRecipients();
  }, [showCompose, userType, projectId]);

  // Load thread messages when a thread is selected
  useEffect(() => {
    if (!selectedThread) {
      setThreadMessages([]);
      return;
    }

    const loadThreadMessages = async () => {
      try {
        // Mark the main message as read
        if (selectedThread.isUnread) {
          const messageRef = doc(db, 'communications', selectedThread.id);
          const currentReadBy = selectedThread.readBy || [];
          if (!currentReadBy.includes(userId)) {
            await updateDoc(messageRef, {
              readBy: [...currentReadBy, userId],
              status: 'read'
            });
          }
        }

        // Load all messages in the thread (simplified to avoid index requirement)
        const threadQuery = query(
          collection(db, 'communications'),
          where('threadId', '==', selectedThread.id)
        );
        
        const unsubscribe = onSnapshot(threadQuery, (snapshot) => {
          const replies = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          // Sort replies by createdAt on client side
          replies.sort((a, b) => {
            const aTime = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
            const bTime = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
            return aTime - bTime;
          });
          setThreadMessages([selectedThread, ...replies]);
        });

        return unsubscribe;
      } catch (error) {
        console.error('Error loading thread:', error);
      }
    };

    const unsubscribe = loadThreadMessages();
    return () => {
      if (unsubscribe && typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [selectedThread, userId]);

  const sendReply = async () => {
    if (!replyMessage.trim() || !selectedThread) return;

    try {
      const replyData = {
        projectId: selectedThread.projectId,
        threadId: selectedThread.id,
        messageType: userType === 'customer' ? 'pfi-customer' : 
                    userType === 'supplier' ? 'pfi-supplier' : 'general',
        senderId: userId,
        senderType: userType,
        senderName: userName,
        senderEmail: userEmail,
        recipientIds: selectedThread.senderType === userType ? 
                      selectedThread.recipientIds : [selectedThread.senderId],
        recipientTypes: selectedThread.senderType === userType ? 
                        selectedThread.recipientTypes : [selectedThread.senderType],
        recipientEmails: selectedThread.senderType === userType ? 
                         selectedThread.recipientEmails : [selectedThread.senderEmail],
        subject: `Re: ${selectedThread.subject}`,
        message: replyMessage,
        status: 'sent',
        priority: 'normal',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        relatedTo: selectedThread.relatedTo || { type: 'general' }
      };

      await addDoc(collection(db, 'communications'), replyData);
      
      // Update parent message status
      await updateDoc(doc(db, 'communications', selectedThread.id), {
        status: 'replied',
        updatedAt: serverTimestamp()
      });

      // Create notification for email/SMS delivery
      const recipientId = selectedThread.senderType === userType ? 
                          selectedThread.recipientIds[0] : selectedThread.senderId;
      const recipientType = selectedThread.senderType === userType ? 
                           selectedThread.recipientTypes[0] : selectedThread.senderType;
      const recipientName = selectedThread.senderType === userType ? 
                           selectedThread.recipientNames[0] : selectedThread.senderName;

      if (recipientId && recipientType !== 'pfi') {
        // Get the correct email address for the recipient
        let recipientEmail = '';
        if (selectedThread.senderType === userType) {
          // We're replying to someone we sent a message to - use the original recipient's email
          // This should be stored in the original message
          recipientEmail = selectedThread.recipientEmails ? selectedThread.recipientEmails[0] : '';
        } else {
          // We're replying to someone who sent us a message - use their email
          recipientEmail = selectedThread.senderEmail || '';
        }
        
        console.log('🔔 Creating reply notification for:', {
          recipientType: recipientType,
          recipientId: recipientId,
          recipientName: recipientName,
          recipientEmail: recipientEmail,
          projectId: selectedThread.projectId,
          subject: `Re: ${selectedThread.subject}`
        });

        // Check if we have a valid email
        if (!recipientEmail || recipientEmail.trim() === '') {
          console.error('❌ No email address found for reply recipient:', recipientName);
          alert(`Warning: Cannot send email notification to ${recipientName} - no email address found.`);
          return;
        }
        
        await addDoc(collection(db, 'notifications'), {
          type: 'communication-reply',
          recipientId: recipientId,
          recipientType: recipientType,
          recipientName: recipientName,
          recipientEmail: recipientEmail,
          projectId: selectedThread.projectId,
          taskId: selectedThread.taskId,
          subject: `Re: ${selectedThread.subject}`,
          message: replyMessage,
          senderName: userName,
          senderType: userType,
          magicLink: userType === 'pfi' && recipientType === 'supplier' ? 
                     `${window.location.origin}/supplier-inbox?supplier=${recipientId}&email=${encodeURIComponent(selectedThread.senderEmail)}&token=auth-${recipientId}` :
                     userType === 'pfi' && recipientType === 'customer' ?
                     `${window.location.origin}/customer-inbox?customer=${recipientId}&email=${encodeURIComponent(selectedThread.senderEmail)}&project=${selectedThread.projectId}&token=auth-${recipientId}` :
                     null,
          status: 'pending',
          createdAt: serverTimestamp()
        });
      }
      
      setReplyMessage('');
    } catch (error) {
      console.error('Error sending reply:', error);
      alert('Failed to send reply');
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !newSubject.trim()) {
      alert('Please enter both subject and message');
      return;
    }
    
    if (userType === 'pfi' && !selectedRecipient) {
      alert('Please select recipients');
      return;
    }

    try {
      // Determine recipient types and IDs based on selection
      let recipientTypes = [];
      let recipientIds = [];
      let recipientNames = [];
      
      if (userType === 'customer') {
        // Customer always sends to PFI
        recipientTypes = ['pfi'];
        recipientIds = ['pfi-team']; // Generic PFI team ID
        recipientNames = ['PFI Team'];
      } else if (userType === 'supplier') {
        // Supplier always sends to PFI
        recipientTypes = ['pfi'];
        recipientIds = ['pfi-team'];
        recipientNames = ['PFI Team'];
      } else if (userType === 'pfi' && selectedRecipient) {
        // PFI sends to specific recipient
        const recipient = availableRecipients.find(r => r.id === selectedRecipient);
        if (recipient) {
          recipientTypes = [recipient.type];
          recipientIds = [recipient.id];
          recipientNames = [recipient.name];
        }
      }

      const messageData = {
        projectId: projectId,
        threadId: null, // This is a new thread
        messageType: userType === 'customer' ? 'pfi-customer' : 
                    userType === 'supplier' ? 'pfi-supplier' : 'general',
        senderId: userId,
        senderType: userType,
        senderName: userName,
        senderEmail: userEmail,
        recipientIds: recipientIds,
        recipientTypes: recipientTypes,
        recipientNames: recipientNames,
        recipientEmails: userType === 'pfi' && selectedRecipient ? 
                         [availableRecipients.find(r => r.id === selectedRecipient)?.email] : [],
        subject: newSubject,
        message: newMessage,
        status: 'sent',
        readBy: [userId], // Sender has read their own message
        priority: 'normal',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        relatedTo: {
          type: 'general'
        },
        replyCount: 0
      };

      await addDoc(collection(db, 'communications'), messageData);
      
      // Create notification for email/SMS delivery
      if (userType === 'pfi' && selectedRecipient) {
        const recipient = availableRecipients.find(r => r.id === selectedRecipient);
        if (recipient && recipient.type !== 'pfi') {
          console.log('🔔 Creating notification for:', {
            recipientType: recipient.type,
            recipientEmail: recipient.email,
            recipientName: recipient.name,
            projectId: projectId,
            subject: newSubject
          });

          // Check if recipient has email address
          if (!recipient.email || recipient.email.trim() === '') {
            console.error('❌ No email address for recipient:', recipient.name);
            alert(`Warning: ${recipient.name} has no email address. They will not receive email notification.`);
          }
          
          await addDoc(collection(db, 'notifications'), {
            type: 'communication-message',
            recipientId: recipient.id,
            recipientType: recipient.type,
            recipientName: recipient.name,
            recipientEmail: recipient.email,
            projectId: projectId,
            subject: newSubject,
            message: newMessage,
            senderName: userName,
            senderType: userType,
            magicLink: recipient.type === 'supplier' ? 
                       `${window.location.origin}/supplier-inbox?supplier=${recipient.id}&email=${encodeURIComponent(recipient.email)}&token=auth-${recipient.id}` :
                       recipient.type === 'customer' && projectId ?
                       `${window.location.origin}/customer-inbox?customer=${recipient.id}&email=${encodeURIComponent(recipient.email)}&project=${projectId}&token=auth-${recipient.id}` :
                       null,
            status: 'pending',
            createdAt: serverTimestamp()
          });
        }
      } else if (userType === 'customer' || userType === 'supplier') {
        // Customer/supplier sending to PFI - could create notification for PFI team if needed
        // For now, PFI will see it in the communications centre
      }
      
      setNewMessage('');
      setNewSubject('');
      setShowCompose(false);
      
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message');
    }
  };

  const formatMessageDate = (timestamp) => {
    if (!timestamp) return '';
    try {
      const date = timestamp?.toDate ? timestamp.toDate() : 
                   timestamp?.seconds ? new Date(timestamp.seconds * 1000) :
                   new Date(timestamp);
      
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      
      if (diffMins < 1) return 'just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      
      return formatDate(date);
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  const getMessageIcon = (messageType) => {
    switch (messageType) {
      case 'info-request': return '❓';
      case 'system-notification': return '🔔';
      case 'pfi-customer': return '💬';
      case 'pfi-supplier': return '🏢';
      default: return '📨';
    }
  };

  // Filter messages based on search and filter type
  const filteredMessages = messages.filter(message => {
    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      if (!message.subject?.toLowerCase().includes(searchLower) &&
          !message.message?.toLowerCase().includes(searchLower) &&
          !message.senderName?.toLowerCase().includes(searchLower)) {
        return false;
      }
    }

    // Type filter
    if (filterType === 'unread' && !message.isUnread) return false;
    
    return true;
  });

  if (loading) {
    return <div className="p-4">Loading communications...</div>;
  }

  return (
    <div className="flex h-[650px] border border-gray-200 rounded-lg overflow-hidden">
      {/* Message List Sidebar */}
      <div className="w-1/3 bg-gray-50 border-r border-gray-200 flex flex-col">
        <div className="border-b border-gray-200 p-4">
          <div className="flex justify-between items-center mb-3">
            <h2 className="text-lg font-semibold text-gray-900">
              {projectId ? 'Project Communications' : 
               userType === 'pfi' ? 'All Communications' : 
               'Communications'}
            </h2>
            {(userType === 'pfi' || userType === 'customer') && (
              <button
                onClick={() => {
                  setShowCompose(!showCompose);
                  setSelectedThread(null);
                }}
                className="bg-pink-600 text-white px-3 py-1 text-sm rounded-lg hover:bg-pink-700"
              >
                Compose
              </button>
            )}
          </div>
          
          {/* Search and Filter */}
          <div className="space-y-2">
            <input
              type="text"
              placeholder="Search messages..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-pink-500"
            />
            <div className="flex space-x-2">
              <button
                onClick={() => setFilterType('all')}
                className={`flex-1 px-2 py-1 text-xs rounded ${filterType === 'all' ? 'bg-pink-100 text-pink-700' : 'bg-gray-100 text-gray-600'}`}
              >
                All
              </button>
              <button
                onClick={() => setFilterType('unread')}
                className={`flex-1 px-2 py-1 text-xs rounded ${filterType === 'unread' ? 'bg-pink-100 text-pink-700' : 'bg-gray-100 text-gray-600'}`}
              >
                Unread
              </button>
            </div>
          </div>
        </div>

      {showCompose && (
        <div className="border-b border-gray-200 p-4 bg-gray-50">
          <div className="space-y-4">
            {userType === 'pfi' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Send To
                </label>
                <select
                  value={selectedRecipient}
                  onChange={(e) => setSelectedRecipient(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
                >
                  <option value="">Select recipient...</option>
                  {availableRecipients.length === 0 ? (
                    <option disabled>Loading recipients...</option>
                  ) : (
                    <>
                      {availableRecipients.filter(r => r.type === 'customer').length > 0 && (
                        <optgroup label="Customers">
                          {availableRecipients
                            .filter(r => r.type === 'customer')
                            .map(recipient => (
                              <option key={recipient.id} value={recipient.id}>
                                {recipient.name} {recipient.email && `(${recipient.email})`}
                              </option>
                            ))}
                        </optgroup>
                      )}
                      {availableRecipients.filter(r => r.type === 'supplier').length > 0 && (
                        <optgroup label="Suppliers">
                          {availableRecipients
                            .filter(r => r.type === 'supplier')
                            .map(recipient => (
                              <option key={recipient.id} value={recipient.id}>
                                {recipient.name} {recipient.email && `(${recipient.email})`}
                              </option>
                            ))}
                        </optgroup>
                      )}
                    </>
                  )}
                </select>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Subject
              </label>
              <input
                type="text"
                value={newSubject}
                onChange={(e) => setNewSubject(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
                placeholder="Enter subject"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Message
              </label>
              <textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
                placeholder="Type your message here..."
              />
            </div>

            <div className="flex space-x-2">
              <button
                onClick={sendMessage}
                disabled={!newSubject || !newMessage || (userType === 'pfi' && !selectedRecipient)}
                className="bg-pink-600 text-white px-4 py-2 rounded-lg hover:bg-pink-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                Send Message
              </button>
              <button
                onClick={() => {
                  setShowCompose(false);
                  setNewSubject('');
                  setNewMessage('');
                  setSelectedRecipient('');
                }}
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

        {/* Message List */}
        <div className="flex-1 overflow-y-auto">
          {filteredMessages.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              {searchTerm ? 'No messages found' : 'No communications yet'}
            </div>
          ) : (
            filteredMessages.map(message => (
              <div 
                key={message.id} 
                className={`p-3 border-b border-gray-100 hover:bg-gray-50 cursor-pointer ${
                  selectedThread?.id === message.id ? 'bg-pink-50' : ''
                } ${message.isUnread ? 'bg-blue-50' : ''}`}
                onClick={() => {
                  console.log('Message clicked:', message);
                  setSelectedThread(message);
                  setShowCompose(false);
                }}
              >
                <div className="flex items-start space-x-2">
                  <div className="text-lg mt-1">
                    {getMessageIcon(message.messageType)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h3 className={`text-sm truncate ${
                          message.isUnread ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'
                        }`}>
                          {message.subject}
                        </h3>
                        <p className="text-xs text-gray-500 truncate">
                          From: {message.senderName} → To: {message.recipientNames?.join(', ') || 'Unknown'}
                        </p>
                      </div>
                      <div className="flex flex-col items-end ml-2">
                        <div className="text-xs text-gray-400">
                          {formatMessageDate(message.createdAt)}
                        </div>
                        {message.projectId && userType === 'pfi' && (
                          <Link 
                            to={`/projects/${message.projectId}`}
                            className="text-xs text-pink-600 hover:text-pink-800 underline mt-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            View Project
                          </Link>
                        )}
                        {message.replyCount > 0 && (
                          <span className="text-xs bg-gray-100 text-gray-600 px-1 py-0.5 rounded mt-1">
                            {message.replyCount} {message.replyCount === 1 ? 'reply' : 'replies'}
                          </span>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                      {message.message}
                    </p>
                    {message.isUnread && (
                      <span className="inline-block mt-1 text-xs bg-blue-100 text-blue-700 px-1 py-0.5 rounded">
                        New
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      {/* Message Detail/Compose Area */}
      <div className="flex-1 bg-white flex flex-col">
        {showCompose ? (
          /* Compose New Message */
          <div className="flex-1 p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">New Message</h3>
            <div className="space-y-4">
              {userType === 'pfi' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Send To
                  </label>
                  <select
                    value={selectedRecipient}
                    onChange={(e) => setSelectedRecipient(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
                  >
                    <option value="">Select recipient...</option>
                    {availableRecipients.length === 0 ? (
                      <option disabled>Loading recipients...</option>
                    ) : (
                      <>
                        {availableRecipients.filter(r => r.type === 'customer').length > 0 && (
                          <optgroup label="Customers">
                            {availableRecipients
                              .filter(r => r.type === 'customer')
                              .map(recipient => (
                                <option key={recipient.id} value={recipient.id}>
                                  {recipient.name} {recipient.email && `(${recipient.email})`}
                                </option>
                              ))}
                          </optgroup>
                        )}
                        {availableRecipients.filter(r => r.type === 'supplier').length > 0 && (
                          <optgroup label="Suppliers">
                            {availableRecipients
                              .filter(r => r.type === 'supplier')
                              .map(recipient => (
                                <option key={recipient.id} value={recipient.id}>
                                  {recipient.name} {recipient.email && `(${recipient.email})`}
                                </option>
                              ))}
                          </optgroup>
                        )}
                      </>
                    )}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Subject
                </label>
                <input
                  type="text"
                  value={newSubject}
                  onChange={(e) => setNewSubject(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
                  placeholder="Enter subject"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message
                </label>
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  rows={10}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
                  placeholder="Type your message here..."
                />
              </div>

              <div className="flex space-x-2">
                <button
                  onClick={sendMessage}
                  disabled={!newSubject || !newMessage || (userType === 'pfi' && !selectedRecipient)}
                  className="bg-pink-600 text-white px-4 py-2 rounded-lg hover:bg-pink-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  Send Message
                </button>
                <button
                  onClick={() => {
                    setShowCompose(false);
                    setNewSubject('');
                    setNewMessage('');
                    setSelectedRecipient('');
                  }}
                  className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        ) : selectedThread ? (
          /* Thread View */
          <div className="flex-1 flex flex-col">
            {/* Thread Header */}
            <div className="border-b border-gray-200 p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900">{selectedThread.subject}</h3>
                  <p className="text-sm text-gray-600">
                    From: {selectedThread.senderName} → To: {selectedThread.recipientNames?.join(', ') || 'Unknown'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {selectedThread.replyCount > 0 && `${selectedThread.replyCount} ${selectedThread.replyCount === 1 ? 'reply' : 'replies'} · `}
                    Started {formatDate(selectedThread.createdAt)}
                  </p>
                </div>
                {selectedThread.projectId && userType === 'pfi' && (
                  <Link 
                    to={`/projects/${selectedThread.projectId}`}
                    className="ml-4 px-3 py-1.5 text-sm bg-pink-100 text-pink-700 hover:bg-pink-200 rounded-lg transition-colors font-medium"
                  >
                    View Project →
                  </Link>
                )}
              </div>
            </div>
            
            {/* Thread Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {threadMessages.map((msg, index) => (
                <div key={msg.id} className={`${
                  msg.senderType === userType ? 'ml-auto' : 'mr-auto'
                } max-w-[70%]`}>
                  <div className={`rounded-lg p-3 ${
                    msg.senderType === userType 
                      ? 'bg-pink-100 text-gray-900' 
                      : 'bg-gray-100 text-gray-900'
                  }`}>
                    <div className="flex items-start justify-between mb-2">
                      <p className="text-sm font-medium">{msg.senderName}</p>
                      <p className="text-xs text-gray-500 ml-2">
                        {formatMessageDate(msg.createdAt)}
                      </p>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Reply Box */}
            <div className="border-t border-gray-200 p-4">
              <div className="flex space-x-2">
                <textarea
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  placeholder="Type your reply..."
                  rows={3}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
                />
                <button
                  onClick={sendReply}
                  disabled={!replyMessage.trim()}
                  className="bg-pink-600 text-white px-4 py-2 rounded-lg hover:bg-pink-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  Send Reply
                </button>
              </div>
            </div>
          </div>
        ) : (
          /* Empty State */
          <div className="flex-1 flex items-center justify-center text-gray-500">
            <div className="text-center">
              <div className="text-5xl mb-4">📬</div>
              <p>Select a message to view or compose a new one</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommunicationsInbox;